<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import AppDrawnCheck from '@/components/primitives/AppDrawnCheck/AppDrawnCheck.vue'
import SetupStepLamp from './SetupStepLamp.vue'
import { useMotionSequence } from '@/composables/useMotionSequence'
import { useStepCompletion } from '@/composables/useStepCompletion'
import { STEP_DONE_MS } from '@/lib/setup/setup-motion'
import type { StepListItem, StepListOrientation } from './step-list-types'

const props = withDefaults(
  defineProps<{
    /** Every step in order, with the state each is drawn in. */
    steps: readonly StepListItem[]
    orientation?: StepListOrientation
    /** Makes each done step a button that emits `open`. */
    pressable?: boolean
    /** The done step shown as pressed. */
    openId?: string | null
  }>(),
  { orientation: 'vertical', pressable: false, openId: null },
)

const emit = defineEmits<{
  /** A done step was pressed. */
  open: [id: string]
}>()

const { t } = useI18n()

const list = ref<HTMLElement | null>(null)
const horizontal = computed(() => props.orientation === 'horizontal')
const lineFill = computed<Keyframe[]>(() =>
  horizontal.value
    ? [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }]
    : [{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }],
)
const motion = useMotionSequence()

type StepPart = 'lamp' | 'flare' | 'number' | 'done' | 'line'

const PART_SELECTOR: Record<StepPart, string> = {
  lamp: '[data-lamp]',
  flare: '[data-lamp-part="flare"]',
  number: '[data-motion="number"]',
  done: '[data-motion="done"]',
  line: '[data-motion="line"]',
}

function part(index: number, name: StepPart): Element | null {
  return list.value?.querySelector(`[data-step-index="${index}"] ${PART_SELECTOR[name]}`) ?? null
}

function checkPath(index: number): Element | null {
  return part(index, 'done')?.querySelector('path') ?? null
}

const FADE_IN: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }]
const FADE_OUT: Keyframe[] = [{ opacity: 1 }, { opacity: 0 }]
const LIGHT_UP: Keyframe[] = [{ '--lamp-on': 0 }, { '--lamp-on': 1 }]
const FLARE: Keyframe[] = [{ opacity: 0 }, { opacity: 1, offset: 0.35 }, { opacity: 0 }]

function playReducedDone(index: number, next: number | null): void {
  const fade = { duration: STEP_DONE_MS.reduced, easing: 'standard' } as const
  motion.play(part(index, 'number'), FADE_OUT, fade)
  motion.play(part(index, 'done'), FADE_IN, fade)
  motion.play(part(index, 'line'), FADE_IN, fade)
  if (next !== null) motion.play(part(next, 'lamp'), LIGHT_UP, fade)
}

function playDone(index: number): void {
  const next = props.steps[index + 1]?.state === 'now' ? index + 1 : null
  if (motion.reduced.value) {
    playReducedDone(index, next)
    return
  }

  motion.play(part(index, 'number'), FADE_OUT, {
    duration: STEP_DONE_MS.numberFade,
    easing: 'soft-exit',
  })
  motion.play(checkPath(index), [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], {
    duration: STEP_DONE_MS.checkDraw,
    delay: STEP_DONE_MS.checkDelay,
    easing: 'standard',
  })
  motion.play(part(index, 'line'), lineFill.value, {
    duration: STEP_DONE_MS.lineFill,
    delay: STEP_DONE_MS.lineDelay,
    easing: 'standard',
  })
  if (next === null) return

  motion.play(part(next, 'lamp'), LIGHT_UP, {
    duration: STEP_DONE_MS.nextLight,
    delay: STEP_DONE_MS.nextDelay,
    easing: 'standard',
  })
  motion.play(part(next, 'flare'), FLARE, {
    duration: STEP_DONE_MS.flare,
    delay: STEP_DONE_MS.flareDelay,
    easing: 'soft',
    fill: 'none',
  })
}

useStepCompletion(() => props.steps, playDone)
</script>

<template>
  <ol
    ref="list"
    :data-orientation="orientation"
    class="flex min-h-0 list-none overflow-hidden"
    :class="horizontal ? 'flex-row' : 'flex-col gap-16 pb-8 pl-12 pr-6 pt-12'"
  >
    <li
      v-for="(entry, index) in steps"
      :key="entry.id"
      :data-step-index="entry.index"
      data-arrival="step"
      class="relative"
      :class="
        horizontal
          ? 'flex min-w-0 flex-1 flex-col items-start gap-2'
          : 'grid grid-cols-[var(--spacing-12)_minmax(0,1fr)] items-start gap-x-6'
      "
    >
      <span
        v-if="index < steps.length - 1"
        aria-hidden="true"
        class="absolute flex"
        :class="
          horizontal
            ? 'top-0 h-12 items-center left-[calc(var(--spacing-12)+var(--spacing-2))] -right-[var(--spacing-2)]'
            : 'left-0 w-12 justify-center top-[calc(var(--spacing-12)+var(--spacing-2))] -bottom-[calc(var(--spacing-12)+var(--spacing-2))]'
        "
      >
        <span
          data-arrival="rail"
          class="relative rounded-full bg-border-strong"
          :class="horizontal ? 'h-1 w-full origin-left' : 'w-1 origin-top'"
        >
          <span
            data-motion="line"
            class="absolute inset-0 rounded-full bg-accent-green"
            :class="[
              horizontal ? 'origin-left' : 'origin-top',
              entry.state === 'done' ? '' : horizontal ? 'scale-x-0' : 'scale-y-0',
            ]"
          />
        </span>
      </span>

      <component
        :is="pressable && entry.state === 'done' ? 'button' : 'div'"
        :type="pressable && entry.state === 'done' ? 'button' : undefined"
        :aria-pressed="pressable && entry.state === 'done' ? openId === entry.id : undefined"
        :class="
          pressable && entry.state === 'done'
            ? [
                'control-press hover-fill rounded-4 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid focus-visible:outline-accent-blue',
                horizontal
                  ? 'flex min-w-0 max-w-full flex-col items-start gap-2'
                  : 'col-span-2 grid grid-cols-subgrid items-start',
              ]
            : 'contents'
        "
        @click="pressable && entry.state === 'done' && emit('open', entry.id)"
      >
        <SetupStepLamp
          :lit="entry.state !== 'todo'"
          :tone="entry.state === 'done' ? 'green' : 'blue'"
          :intensity="entry.state === 'now' ? 'bright' : 'steady'"
        >
          <span data-motion="number" :class="entry.state === 'done' ? 'opacity-0' : ''">
            {{ entry.index + 1 }}
          </span>
          <span data-motion="done" class="grid place-items-center">
            <AppDrawnCheck :drawn="entry.state === 'done'" />
          </span>
        </SetupStepLamp>
        <span class="sr-only">
          {{ entry.state === 'done' ? t('setup.stepDone') : entry.index + 1 }}
        </span>

        <span class="flex min-w-0 max-w-full flex-col gap-1" :class="horizontal ? 'pr-4' : 'pt-1'">
          <span
            class="text-body-sm-emphasized"
            :class="[
              entry.state === 'done' ? 'text-text-secondary' : 'text-text-primary',
              horizontal ? 'truncate' : '',
            ]"
          >
            {{ entry.title }}
          </span>
          <span class="text-caption text-text-tertiary" :class="horizontal ? 'truncate' : ''">
            {{ entry.caption }}
          </span>
        </span>
      </component>
    </li>
  </ol>
</template>
